import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import axiosConfig from "../Service/axiosConfig";
import tableCustomStyles from "../Components/PopupModal/tableCustomStyles";
import { useDebounce } from "../Utils/useDebounce";

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const CastingPackagesList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowPerPage, setRowPerPage] = useState(20);
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [planFor, setPlanFor] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockBuyer, setUnlockBuyer] = useState(null);
  const [unlockRows, setUnlockRows] = useState([]);
  const [unlockTotal, setUnlockTotal] = useState(0);
  const [unlockPage, setUnlockPage] = useState(1);

  const fetchPackages = async (page = currentPage, pageSize = rowPerPage) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (activeOnly) params.set("active", "1");
      if (planFor) params.set("plan_for", planFor);
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());

      const response = await axiosConfig.get(
        `/accounts/admin/casting-packages/?${params.toString()}`
      );
      setData(response.data.results || []);
      setTotalRows(response.data.count || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error(error);
      setData([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnlocks = async (profileId, page = 1) => {
    setUnlockLoading(true);
    try {
      const response = await axiosConfig.get(
        `/accounts/admin/casting-packages/${profileId}/unlocks/?page=${page}&page_size=20`
      );
      setUnlockBuyer(response.data.buyer || null);
      setUnlockRows(response.data.results || []);
      setUnlockTotal(response.data.count || 0);
      setUnlockPage(page);
    } catch (error) {
      console.error(error);
      setUnlockBuyer(null);
      setUnlockRows([]);
      setUnlockTotal(0);
    } finally {
      setUnlockLoading(false);
    }
  };

  const openUnlocks = (row) => {
    if (!row?.profile_id) return;
    setUnlockModalOpen(true);
    setUnlockBuyer({
      profile_id: row.profile_id,
      hcc_id: row.hcc_id,
      full_name: row.full_name,
      unlock_credits: row.unlock_credits,
    });
    fetchUnlocks(row.profile_id, 1);
  };

  useEffect(() => {
    fetchPackages(1, rowPerPage);
  }, [debouncedSearch, activeOnly, planFor, rowPerPage]);

  const columns = [
    {
      name: "Buyer",
      selector: (row) => row.hcc_id,
      grow: 1.4,
      wrap: true,
      cell: (row) => (
        <button
          type="button"
          className="btn btn-link p-0 text-start"
          onClick={() => {
            if (row.hcc_id) navigate(`/dashboard/users/${row.hcc_id}/edit`);
          }}
        >
          <div>
            <b>{row.full_name || "--"}</b>
            <br />
            {row.hcc_id || "--"}
          </div>
        </button>
      ),
    },
    {
      name: "Package",
      selector: (row) => row.plan_name,
      grow: 1,
      wrap: true,
      cell: (row) => (
        <div>
          <b>{row.plan_name || "--"}</b>
          <br />
          <span style={{ color: "#6b7280", fontSize: 12 }}>
            {row.plan_for === "casting" ? "Casting package" : "Unlock contacts"}
          </span>
        </div>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.is_active,
      width: "100px",
      cell: (row) => (
        <span className={row.is_active ? "text-success" : "text-muted"}>
          {row.is_active ? "Active" : "Expired"}
        </span>
      ),
    },
    {
      name: "Credits left",
      selector: (row) => row.unlock_credits,
      width: "110px",
      cell: (row) => row.unlock_credits ?? 0,
    },
    {
      name: "Contacts unlocked",
      selector: (row) => row.unlocks_count,
      width: "140px",
      cell: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => openUnlocks(row)}
          disabled={!row.unlocks_count}
        >
          {row.unlocks_count || 0}
        </button>
      ),
    },
    {
      name: "Valid",
      selector: (row) => row.end_date,
      grow: 1.2,
      wrap: true,
      cell: (row) => (
        <div style={{ fontSize: 12, lineHeight: 1.35 }}>
          <div>From: {formatDateTime(row.start_date)}</div>
          <div>To: {formatDateTime(row.end_date)}</div>
        </div>
      ),
    },
  ];

  const unlockColumns = [
    {
      name: "Photo",
      selector: (row) => row.unlocked_profile_pic,
      width: "72px",
      cell: (row) =>
        row.unlocked_profile_pic ? (
          <img
            src={row.unlocked_profile_pic}
            alt={row.unlocked_full_name || row.unlocked_hcc_id || "profile"}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
        ) : (
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "#e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#6b7280",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {(row.unlocked_full_name || "?").charAt(0).toUpperCase()}
          </div>
        ),
    },
    {
      name: "Unlocked profile",
      selector: (row) => row.unlocked_hcc_id,
      grow: 1.5,
      wrap: true,
      cell: (row) => (
        <button
          type="button"
          className="btn btn-link p-0 text-start"
          onClick={() => {
            if (row.unlocked_hcc_id) {
              navigate(`/dashboard/users/${row.unlocked_hcc_id}/edit`);
            }
          }}
        >
          <div>
            <b>{row.unlocked_full_name || "--"}</b>
            <br />
            {row.unlocked_hcc_id || "--"}
          </div>
        </button>
      ),
    },
    {
      name: "Unlocked at",
      selector: (row) => row.unlocked_at,
      grow: 1,
      cell: (row) => formatDateTime(row.unlocked_at),
    },
    {
      name: "Source",
      selector: (row) => row.access_source,
      width: "100px",
      cell: (row) => row.access_source || "--",
    },
  ];

  return (
    <>
      <div className="child-sidenav">
        <div className="childnav-content">
          <div className="marginTop">
            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
              <h5 className="mb-0 me-auto">Casting Packages</h5>
              <input
                className="form-control"
                style={{ maxWidth: 260 }}
                placeholder="Search name / ICC ID / mobile"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select
                className="form-select"
                style={{ maxWidth: 180 }}
                value={planFor}
                onChange={(e) => setPlanFor(e.target.value)}
              >
                <option value="">All packages</option>
                <option value="casting">Casting package</option>
                <option value="unlock_contact">Unlock contacts</option>
              </select>
              <label className="d-flex align-items-center gap-2 mb-0">
                <input
                  type="checkbox"
                  checked={activeOnly}
                  onChange={(e) => setActiveOnly(e.target.checked)}
                />
                Active only
              </label>
            </div>

            <DataTable
              columns={columns}
              data={data}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={rowPerPage}
              paginationDefaultPage={currentPage}
              onChangePage={(page) => fetchPackages(page, rowPerPage)}
              onChangeRowsPerPage={(next) => setRowPerPage(next)}
              paginationRowsPerPageOptions={[10, 20, 25, 30, 40, 50]}
              customStyles={tableCustomStyles}
              progressPending={loading}
              progressComponent={
                <div className="loader-container">
                  <img
                    src={require("../Components/FormParser/images/loader2.gif")}
                    alt="Loading..."
                  />
                </div>
              }
              noDataComponent="No casting package purchases found."
            />
          </div>
        </div>
      </div>

      <Modal
        show={unlockModalOpen}
        onHide={() => setUnlockModalOpen(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Unlocked contacts
            {unlockBuyer?.hcc_id ? ` — ${unlockBuyer.full_name} (${unlockBuyer.hcc_id})` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Credits remaining: <b>{unlockBuyer?.unlock_credits ?? 0}</b>
          </p>
          <DataTable
            columns={unlockColumns}
            data={unlockRows}
            pagination
            paginationServer
            paginationTotalRows={unlockTotal}
            paginationPerPage={20}
            paginationDefaultPage={unlockPage}
            onChangePage={(page) => {
              if (unlockBuyer?.profile_id) fetchUnlocks(unlockBuyer.profile_id, page);
            }}
            customStyles={tableCustomStyles}
            progressPending={unlockLoading}
            noDataComponent="No contacts unlocked yet."
            dense
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export { CastingPackagesList };
